import { useState, type FormEvent } from 'react';
import { isElectron, useSendMagicAuthCode, useWorkosCallback } from '@fluxter/shared';
import { useNavigate } from '@tanstack/react-router';
import { tokenStore } from '@/lib/api';
import { isAccessGateBlocked } from '@/lib/auth-errors';
import {
  beginDesktopSignIn,
  beginWebSignIn,
  stashMagicAuthEmail,
} from '@/lib/sign-in';
import { consumePostLoginReturnUrl } from '@/lib/post-login-redirect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AppleIcon, GoogleIcon } from '@/components/BrandIcon';
import { AuthHeader, AuthShell } from '@/components/AuthShell';

const deviceName = isElectron() ? 'desktop' : 'web';

/**
 * Slice 1+2 own-UI: replaces the WorkOS AuthKit hosted-page button
 * with three sign-in surfaces controlled by Fluxter:
 *   - Continue with Google (functional; uses WorkOS provider hint
 *     `'GoogleOAuth'` to skip the AuthKit picker)
 *   - Continue with Apple (disabled placeholder; the Apple Service
 *     ID isn't set up yet — surfaces the button so the layout is
 *     final, hides the implementation detail behind a tooltip)
 *   - Email + 6-digit code (POST /auth/magic-link/send → /auth/code-sent)
 *
 * The desktop (Electron) flow keeps the loopback OAuth dance for
 * Google because magic-auth-by-email doesn't make sense as a
 * primary flow inside a native shell. Magic auth on desktop is
 * intentionally not wired here — desktop users tap "Continue with
 * Google" and that's it.
 */
export const LoginPage = () => {
  const [pending, setPending] = useState<'google' | 'magic' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();

  const callback = useWorkosCallback(async (token) => {
    await tokenStore.set(token);
  });
  const sendMagic = useSendMagicAuthCode();

  /* Continue with Google */
  const handleGoogle = async () => {
    setError(null);
    setPending('google');

    try {
      // Phase 3.5: explicit `provider=GoogleOAuth` skips the AuthKit
      // hosted-page redirect entirely (no more invisible round-trip
      // through `accounts.workos.com` in the network tab). Multi-org
      // users used to need AuthKit's picker to disambiguate; the API
      // now resolves that server-side by detecting WorkOS's
      // `organization_selection_required` response and re-issuing
      // the auth-code exchange under the org-selection grant scoped
      // to the configured Fluxter org.
      if (isElectron()) {
        const { code, codeVerifier } = await beginDesktopSignIn('GoogleOAuth');
        await callback.mutateAsync({
          code,
          code_verifier: codeVerifier,
          device_name: deviceName,
        });
        const returnUrl = consumePostLoginReturnUrl();
        if (returnUrl) {
          window.location.assign(returnUrl);
        } else {
          await navigate({ to: '/' });
        }
      } else {
        await beginWebSignIn('GoogleOAuth');
      }
    } catch (e) {
      // Org-gate / beta-gate rejection (desktop flow only — the web
      // flow's gate check happens on /auth/callback). Bounce to
      // the polished /access-denied page rather than showing an
      // inline error.
      if (isAccessGateBlocked(e)) {
        await navigate({ to: '/access-denied', replace: true });
        return;
      }
      setError(e instanceof Error ? e.message : 'Sign-in failed.');
      setPending(null);
    }
  };

  /* Continue with email (magic auth) */
  const handleMagic = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    setError(null);
    setPending('magic');

    try {
      await sendMagic.mutateAsync({ email: trimmed });
      // Always succeeds (the API returns 202 even for unknown
      // emails). Stash the email so /auth/code-sent can read it
      // back through a refresh, then forward.
      stashMagicAuthEmail(trimmed);
      await navigate({ to: '/auth/code-sent' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send code. Try again.');
      setPending(null);
    }
  };

  return (
    <AuthShell
      width="sm"
      header={
        <AuthHeader
          title="Sign in to Fluxter"
          subtitle="Continue with Google, Apple, or your work email."
        />
      }
    >
      <div className="space-y-3">
        <Button
          type="button"
          variant="outline"
          onClick={handleGoogle}
          disabled={pending !== null}
          className="w-full justify-center gap-2"
        >
          <GoogleIcon className="size-4" />
          {pending === 'google' ? 'Opening Google…' : 'Continue with Google'}
        </Button>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {/*
                <span> wrapper because Radix's TooltipTrigger needs a
                pointer-receiving element, but we want the underlying
                button visibly disabled. The span receives hover, the
                button stays inert.
              */}
              <span tabIndex={0} className="block w-full">
                <Button
                  type="button"
                  variant="outline"
                  disabled
                  className="pointer-events-none w-full justify-center gap-2 opacity-60"
                >
                  <AppleIcon className="size-4" />
                  Continue with Apple
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Coming soon</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="relative">
        <div className="border-border absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-[var(--glass-card-bg)] text-foreground-faint px-2 text-[11px] uppercase tracking-wide">
            or with email
          </span>
        </div>
      </div>

      <form className="space-y-3" onSubmit={handleMagic} noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-[12px] font-medium">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@company.com"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={pending !== null}
          />
        </div>

        <Button
          type="submit"
          disabled={pending !== null || email.trim().length === 0}
          className="w-full"
        >
          {pending === 'magic' ? 'Sending code…' : 'Email me a sign-in code'}
        </Button>
      </form>

      {error ? (
        <p className="text-destructive text-center text-[12.5px]" role="alert">
          {error}
        </p>
      ) : null}
    </AuthShell>
  );
};
